import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowUp, Briefcase } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import HeroSlider from '../components/HeroSlider';
import GettingStartedSection from '../components/GettingStartedSection';
import CTASection from '../components/CTASection';
import MentalHealthAwareness from '../components/MentalHealthAwareness';
import Footer from '../components/Footer';
import AppointmentModal from '../components/AppointmentModal';
import SelfAssessmentModal from '../components/SelfAssessmentModal';
import ReferralFormModal from '../components/ReferralFormModal';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';

const HOMEPAGE_JSONLD = {
  "@context": "https://schema.org",
  "@type": ["MedicalBusiness", "LocalBusiness"],
  "name": "SDORA Wellbeing LLC",
  "alternateName": "SDORA Wellbeing",
  "description": "CARF-accredited outpatient mental health clinic providing therapy, psychiatric rehabilitation (PRP), medication management, case management, and telehealth services in Pikesville and Baltimore County, Maryland.",
  "url": "https://sdorawellbeing.org",
  "telephone": "+1-877-772-5150",
  "faxNumber": "+1-667-400-5108",
  "email": "info@sdorawellbeing.org",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "1700 Reisterstown Rd, Suite 109",
    "addressLocality": "Pikesville",
    "addressRegion": "MD",
    "postalCode": "21208",
    "addressCountry": "US"
  },
  "geo": { "@type": "GeoCoordinates", "latitude": 39.3687, "longitude": -76.7225 },
  "areaServed": [
    { "@type": "City", "name": "Baltimore" },
    { "@type": "AdministrativeArea", "name": "Baltimore County" },
    { "@type": "State", "name": "Maryland" }
  ],
  "openingHoursSpecification": [
    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "09:00", "closes": "17:00" }
  ],
  "medicalSpecialty": ["Psychiatric", "CommunityHealth"],
  "availableService": [
    { "@type": "MedicalTherapy", "name": "Individual Therapy" },
    { "@type": "MedicalTherapy", "name": "Family Therapy" },
    { "@type": "MedicalTherapy", "name": "Medication Management" },
    { "@type": "MedicalTherapy", "name": "Psychiatric Rehabilitation (PRP)" },
    { "@type": "MedicalTherapy", "name": "Case Management" },
    { "@type": "MedicalTherapy", "name": "Comprehensive Assessment" },
    { "@type": "MedicalTherapy", "name": "Telehealth Services" }
  ],
  "paymentAccepted": "Medicaid, Medicare, Insurance",
  "priceRange": "$$",
  "image": "https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/sthkmul7_SDORA_Logo-copy.png",
  "sameAs": [
    "https://www.facebook.com/p/SDORA-Wellbeing-LLC-100086663974006/",
    "https://www.instagram.com/sdorawellbeing/"
  ]
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showSelfAssessmentModal, setShowSelfAssessmentModal] = useState(false);
  const [showReferralForm, setShowReferralForm] = useState(false);
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const insuranceRes = await axios.get(`${API}/insurance`);
        setInsuranceProviders(insuranceRes.data);
      } catch {
        // Error handled silently
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setShowScrollTop(window.scrollY > 600);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        path="/"
        description="SDORA Wellbeing provides outpatient mental health therapy (OMHC), psychiatric rehabilitation (PRP), medication management, and case management in Pikesville and Baltimore County, Maryland. CARF accredited. Accepting Medicaid and most insurance. Telehealth and mobile services available."
        jsonLd={HOMEPAGE_JSONLD}
      />
      <Navigation onOpenReferralForm={() => setShowReferralForm(true)} />
      <HeroSlider 
        onBookAppointment={() => setShowAppointmentModal(true)}
        onTakeSelfAssessment={() => navigate('/self-assessment')}
      />

      {/* VACCN Provider Banner */}
      <ScrollReveal direction="up" delay={0.1}>
        <div className="bg-blue-700 py-3" data-testid="vaccn-badge">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-3">
            <p className="text-white text-base md:text-lg font-semibold text-center">
              * SDORA Wellbeing LLC is an Approved VA Community Care Network (VACCN Provider) *
            </p>
          </div>
        </div>
      </ScrollReveal>

      {/* We're Hiring Banner */}
      <ScrollReveal direction="up" delay={0.15}>
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 py-3">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/careers"
              className="flex items-center justify-center gap-3 text-white hover:text-green-100 transition-colors group"
              data-testid="hiring-banner"
            >
              <Briefcase className="w-5 h-5" />
              <span className="font-semibold text-base">We're Hiring! Join our team and make a difference in mental health.</span>
              <span className="text-sm bg-white/20 px-3 py-1 rounded-full font-bold group-hover:bg-white/30 transition-colors">View Openings</span>
            </Link>
          </div>
        </div>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={0.05}>
        <GettingStartedSection onBookAppointment={() => setShowAppointmentModal(true)} />
      </ScrollReveal>

      <ScrollReveal direction="scale" delay={0.1}>
        <CTASection onBookAppointment={() => setShowAppointmentModal(true)} />
      </ScrollReveal>

      <ScrollReveal direction="up" delay={0.05}>
        <MentalHealthAwareness />
      </ScrollReveal>

      <ScrollReveal direction="up" delay={0.1}>
        <Footer />
      </ScrollReveal>
      
      {showAppointmentModal && (
        <AppointmentModal 
          onClose={() => setShowAppointmentModal(false)}
          insuranceProviders={insuranceProviders}
        />
      )}
      
      {showSelfAssessmentModal && (
        <SelfAssessmentModal 
          onClose={() => {
            setShowSelfAssessmentModal(false);
            setShowAppointmentModal(true);
          }}
        />
      )}

      {showReferralForm && (
        <ReferralFormModal 
          onClose={() => setShowReferralForm(false)}
        />
      )}

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        aria-label="Scroll to top"
        data-testid="scroll-to-top-btn"
        className={`fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-blue-600 text-white shadow-lg flex items-center justify-center hover:bg-blue-700 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 ${
          showScrollTop
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <ArrowUp className="w-5 h-5" />
      </button>
    </div>
  );
};

export default HomePage;
